import React, { useEffect } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface AuthProtectedProps {
  children: React.ReactNode;
}

/**
 * AuthProtected Component
 * Protects routes that require authentication
 * Redirects to /login if user is not authenticated
 * Redirects to /setup if setup is not completed
 */
const AuthProtected: React.FC<AuthProtectedProps> = ({ children }) => {
  const { isAuthenticated, loading, user, updateUser } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Check for eBay callback params that indicate setup is complete
  const isSyncing = searchParams.get('syncing') === 'true';
  const ebayConnected = searchParams.get('ebay_connected') === 'true';
  const isFromEbayCallback = isSyncing || ebayConnected;

  // Update user context when coming from eBay callback
  useEffect(() => {
    if (isFromEbayCallback && user && !user.setup_completed) {
      // Update local user state to reflect setup completion
      updateUser({ setup_completed: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFromEbayCallback, user?.setup_completed]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted URL for redirecting after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if setup is completed (skip for admin users and setup-related pages)
  const isAdmin = user?.is_admin || user?.role === 'admin';
  const isSetupPage = location.pathname === '/setup';
  const isSubscriptionPage = location.pathname === '/subscription';
  const isSetupWizardPage = location.pathname === '/setup-wizard';
  const setupCompleted = user?.setup_completed || isFromEbayCallback;

  // Pages allowed during setup flow
  const isSetupFlowPage = isSetupPage || isSubscriptionPage || isSetupWizardPage;

  // Redirect to setup if not completed (except for admins, setup flow pages, and eBay callback)
  if (!isAdmin && !isSetupFlowPage && !setupCompleted) {
    return <Navigate to="/setup" replace />;
  }

  // If on setup page but setup is already completed, redirect to dashboard
  if (isSetupPage && setupCompleted && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Subscription expired/cancelled gate (including expired trial)
  // "cancelling" is treated as active (access until subscription_end_date)
  const isTrialExpired = user?.subscription_status === "trial" && user?.trial_end_date && new Date(user.trial_end_date) < new Date();
  const isCancellingExpired = user?.subscription_status === "cancelling" && user?.subscription_end_date && new Date(user.subscription_end_date) < new Date();
  const isSubscriptionInactive = user?.subscription_status === "expired" || user?.subscription_status === "cancelled" || isTrialExpired || isCancellingExpired;
  const subscriptionExemptPaths = ["/subscription", "/profile", "/logout", "/settings"];
  const isExemptPage = isSetupFlowPage || subscriptionExemptPaths.some(path => location.pathname.startsWith(path));

  if (isSubscriptionInactive && !isAdmin && !isExemptPage) {
    return <Navigate to="/subscription?expired=true" replace />;
  }

  // Render children if authenticated and setup completed
  return <>{children}</>;
};

export default AuthProtected;
